// Pause menu — modal overlay on RunScene. Buttons: Resume, Mute / Unmute,
// Codex, End Run. Sized for both mouse + touch (44px+ buttons).
//
// Same await/resolve pattern as the breeding panel: open() returns a
// Promise that resolves with the chosen action.

import Phaser from 'phaser';
import { audio } from '../audio';
import { isTouchDevice } from '../input/device';

export type PauseAction = 'resume' | 'codex' | 'endRun';

export class PauseMenu {
  private container?: Phaser.GameObjects.Container;
  private resolveFn?: (action: PauseAction) => void;

  constructor(private readonly scene: Phaser.Scene) {}

  isOpen(): boolean {
    return !!this.container;
  }

  open(): Promise<PauseAction> {
    return new Promise((resolve) => {
      this.resolveFn = resolve;
      this.build();
    });
  }

  destroy(): void {
    this.container?.destroy();
    this.container = undefined;
  }

  private build(): void {
    const { width, height } = this.scene.scale.gameSize;
    const touch = isTouchDevice();
    const c = this.scene.add.container(0, 0).setDepth(2500);

    const bg = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6);
    bg.setInteractive();
    c.add(bg);

    const panelW = Math.min(touch ? 320 : 280, width - 40);
    const btnH = touch ? 56 : 44;
    const gap = 12;
    const titleH = 50;
    const panelH = titleH + 4 * (btnH + gap) - gap + 24;
    const panelX = (width - panelW) / 2;
    const panelY = (height - panelH) / 2;

    const panel = this.scene.add
      .rectangle(panelX, panelY, panelW, panelH, 0x1a1d23, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xff9ec4);
    c.add(panel);

    const title = this.scene.add
      .text(panelX + panelW / 2, panelY + 24, 'paused', {
        fontFamily: 'system-ui',
        fontSize: touch ? '22px' : '20px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    c.add(title);

    const muteLabel = () => (audio.isMuted() ? 'unmute audio' : 'mute audio');

    const buttons: { label: () => string; onClick: () => void }[] = [
      { label: () => 'resume', onClick: () => this.resolve('resume') },
      {
        label: muteLabel,
        onClick: () => {
          audio.toggleMute();
          // Redraw label in place.
          const idx = 1;
          const node = c.list[2 + idx * 3 + 1] as Phaser.GameObjects.Text | undefined;
          if (node) node.setText(muteLabel());
        },
      },
      {
        label: () => 'open codex',
        onClick: () => {
          window.open('./help.html', '_blank');
        },
      },
      { label: () => 'end run', onClick: () => this.resolve('endRun') },
    ];

    let y = panelY + titleH + 8;
    for (const b of buttons) {
      const btn = this.makeButton(panelX + 16, y, panelW - 32, btnH, b.label(), b.onClick, touch);
      c.add(btn);
      y += btnH + gap;
    }

    // Version footer — bottom-center of panel.
    const ver = this.scene.add
      .text(panelX + panelW / 2, panelY + panelH - 8, `v${__APP_VERSION__}`, {
        fontFamily: 'system-ui',
        fontSize: '11px',
        color: '#555555',
      })
      .setOrigin(0.5, 1);
    c.add(ver);

    // ESC closes (resume) on desktop.
    this.scene.input.keyboard?.once('keydown-ESC', () => this.resolve('resume'));

    this.container = c;
  }

  private makeButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    onClick: () => void,
    touch: boolean,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const rect = this.scene.add
      .rectangle(0, 0, w, h, 0x3a3f4a, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6a6f7a);
    const t = this.scene.add
      .text(w / 2, h / 2, label, {
        fontFamily: 'system-ui',
        fontSize: touch ? '18px' : '15px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    rect.setInteractive({ useHandCursor: true });
    rect.on('pointerover', () => rect.setFillStyle(0x4a4f5a, 1));
    rect.on('pointerout', () => rect.setFillStyle(0x3a3f4a, 1));
    rect.on('pointerup', () => onClick());
    c.add([rect, t]);
    return c;
  }

  private resolve(action: PauseAction): void {
    const fn = this.resolveFn;
    this.resolveFn = undefined;
    this.destroy();
    fn?.(action);
  }
}
